/**
 * PDFExportService - Generate PDF exports for session notes
 * Uses @react-pdf/renderer for professional PDF generation
 */

import React from 'react';
import { Document, Page, Text, View, StyleSheet, pdf, Image } from '@react-pdf/renderer';
import { SessionNote } from '../models/SessionNote';
import { saveAs } from 'file-saver';

// PDF Styles
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 11,
    fontFamily: 'Helvetica',
  },
  watermark: {
    position: 'absolute',
    fontSize: 60,
    color: '#f0f0f0',
    transform: 'rotate(-45deg)',
    top: '40%',
    left: '20%',
    opacity: 0.3,
  },
  header: {
    marginBottom: 20,
    borderBottom: '2 solid #4F6367',
    paddingBottom: 10,
  },
  title: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#4F6367',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 10,
    color: '#666',
  },
  section: {
    marginTop: 15,
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#4F6367',
    marginBottom: 8,
    borderBottom: '1 solid #ddd',
    paddingBottom: 3,
  },
  label: {
    fontSize: 9,
    color: '#666',
    marginBottom: 2,
  },
  value: {
    fontSize: 11,
    marginBottom: 8,
  },
  table: {
    width: '100%',
    marginTop: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '1 solid #ddd',
    paddingVertical: 5,
  },
  tableHeader: {
    backgroundColor: '#f0f0f0',
    fontWeight: 'bold',
  },
  tableCol: {
    flex: 1,
    fontSize: 10,
  },
  signature: {
    marginTop: 20,
    padding: 10,
    border: '1 solid #ddd',
    borderRadius: 4,
  },
  signatureImage: {
    width: 200,
    height: 60,
    marginTop: 5,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 40,
    right: 40,
    fontSize: 8,
    color: '#999',
    textAlign: 'center',
    borderTop: '1 solid #ddd',
    paddingTop: 10,
  },
  disclaimer: {
    marginTop: 20,
    padding: 10,
    backgroundColor: '#fff3cd',
    border: '1 solid #ffc107',
    fontSize: 9,
  },
});

// PDF Document Component
interface SessionNotePDFProps {
  note: SessionNote;
  companyName?: string;
  companyLogo?: string;
}

const SessionNotePDF: React.FC<SessionNotePDFProps> = ({ note, companyName, companyLogo }) => {
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleString();
  };

  const formatTime = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate();
    return date.toLocaleTimeString();
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Watermark */}
        <Text style={styles.watermark}>CONFIDENTIAL</Text>

        {/* Header */}
        <View style={styles.header}>
          {companyLogo && <Image src={companyLogo} style={{ width: 100, marginBottom: 10 }} />}
          <Text style={styles.title}>ABA Therapy Session Note</Text>
          <Text style={styles.subtitle}>
            {companyName || 'Behavioral Health Services'} | Document ID: {note.id.substring(0, 8)}
          </Text>
          <Text style={styles.subtitle}>Generated: {new Date().toLocaleString()}</Text>
        </View>

        {/* Session Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Session Information</Text>
          <Text style={styles.label}>Session Date</Text>
          <Text style={styles.value}>{formatDate(note.timestamps.sessionStart)}</Text>

          <Text style={styles.label}>RBT</Text>
          <Text style={styles.value}>
            {note.rbtName} {note.rbtCredentials ? `(${note.rbtCredentials})` : ''}
          </Text>

          <Text style={styles.label}>Supervisor</Text>
          <Text style={styles.value}>
            {note.supervisorName} {note.supervisorCredentials ? `(${note.supervisorCredentials})` : ''}
          </Text>

          {note.timestamps.sessionStart && note.timestamps.sessionEnd && (
            <>
              <Text style={styles.label}>Session Duration</Text>
              <Text style={styles.value}>
                {formatTime(note.timestamps.sessionStart)} - {formatTime(note.timestamps.sessionEnd)}
              </Text>
            </>
          )}
        </View>

        {/* Location Verification */}
        {note.locationVerification && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Location Verification</Text>
            <Text style={styles.label}>Check-in Status</Text>
            <Text style={styles.value}>
              {note.locationVerification.status.toUpperCase()}
              {note.locationVerification.distance &&
                ` (${note.locationVerification.distance.toFixed(2)} mi from appointment)`}
            </Text>
            {note.locationVerification.appointmentAddress && (
              <>
                <Text style={styles.label}>Appointment Address</Text>
                <Text style={styles.value}>{note.locationVerification.appointmentAddress}</Text>
              </>
            )}
          </View>
        )}

        {/* Clinical Notes */}
        {note.supervisorFinal && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Clinical Documentation</Text>
            <Text style={styles.label}>Clinical Notes</Text>
            <Text style={styles.value}>{note.supervisorFinal.clinicalNotes}</Text>

            {note.supervisorFinal.liveStreamObservations && (
              <>
                <Text style={styles.label}>Live Stream Observations</Text>
                <Text style={styles.value}>{note.supervisorFinal.liveStreamObservations}</Text>
              </>
            )}

            {note.supervisorFinal.supervisionFeedback && (
              <>
                <Text style={styles.label}>Supervision Feedback</Text>
                <Text style={styles.value}>{note.supervisorFinal.supervisionFeedback}</Text>
              </>
            )}

            {note.supervisorFinal.recommendationsForRBT && (
              <>
                <Text style={styles.label}>Recommendations for RBT</Text>
                <Text style={styles.value}>{note.supervisorFinal.recommendationsForRBT}</Text>
              </>
            )}
          </View>
        )}

        {/* Program Data */}
        {note.rbtDraft?.programData && note.rbtDraft.programData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Program Data</Text>
            {note.rbtDraft.programData.map((program, index) => (
              <View key={index} style={{ marginBottom: 10 }}>
                <Text style={styles.label}>{program.programName}</Text>
                <Text style={styles.value}>
                  Trials: {program.trialsCompleted} | Correct: {program.correctResponses} |
                  Incorrect: {program.incorrectResponses} | Prompt Level: {program.promptLevel}
                </Text>
                {program.notes && <Text style={{ fontSize: 10 }}>{program.notes}</Text>}
              </View>
            ))}
          </View>
        )}

        {/* Behavior Data */}
        {note.rbtDraft?.behaviorData && note.rbtDraft.behaviorData.length > 0 && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Behavior Data</Text>
            {note.rbtDraft.behaviorData.map((behavior, index) => (
              <View key={index} style={{ marginBottom: 10 }}>
                <Text style={styles.label}>{behavior.behaviorName}</Text>
                <Text style={styles.value}>
                  Frequency: {behavior.frequency}
                  {behavior.duration && ` | Duration: ${behavior.duration} min`}
                  {behavior.intensity && ` | Intensity: ${behavior.intensity}`}
                </Text>
                <Text style={{ fontSize: 9, marginTop: 2 }}>
                  Antecedents: {behavior.antecedents}
                </Text>
                <Text style={{ fontSize: 9 }}>Consequences: {behavior.consequences}</Text>
              </View>
            ))}
          </View>
        )}

        {/* Supervision Information */}
        {note.supervision && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Supervision Information</Text>
            <Text style={styles.label}>Method</Text>
            <Text style={styles.value}>{note.supervision.supervisionMethod}</Text>

            <Text style={styles.label}>Duration</Text>
            <Text style={styles.value}>{note.supervision.supervisionDuration} minutes</Text>

            {note.supervision.totalViewingTime && (
              <>
                <Text style={styles.label}>Total Viewing Time (All Sessions)</Text>
                <Text style={styles.value}>{note.supervision.totalViewingTime} minutes</Text>
              </>
            )}

            <Text style={styles.label}>Billing Code</Text>
            <Text style={styles.value}>{note.supervision.billingCode}</Text>
          </View>
        )}

        {/* Digital Signatures */}
        {(note.signatures?.rbt || note.signatures?.supervisor) && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Digital Signatures</Text>

            {note.signatures.rbt && (
              <View style={styles.signature}>
                <Text style={styles.label}>RBT Signature</Text>
                <Text style={styles.value}>
                  {note.signatures.rbt.signedBy} ({note.signatures.rbt.credentials})
                </Text>
                <Text style={{ fontSize: 9 }}>Signed: {formatDate(note.signatures.rbt.signedAt)}</Text>
                {note.signatures.rbt.signatureImage && (
                  <Image src={note.signatures.rbt.signatureImage} style={styles.signatureImage} />
                )}
              </View>
            )}

            {note.signatures.supervisor && (
              <View style={styles.signature}>
                <Text style={styles.label}>Supervisor Signature</Text>
                <Text style={styles.value}>
                  {note.signatures.supervisor.signedBy} ({note.signatures.supervisor.credentials})
                </Text>
                <Text style={{ fontSize: 9 }}>
                  Signed: {formatDate(note.signatures.supervisor.signedAt)}
                </Text>
                {note.signatures.supervisor.signatureImage && (
                  <Image
                    src={note.signatures.supervisor.signatureImage}
                    style={styles.signatureImage}
                  />
                )}
              </View>
            )}
          </View>
        )}

        {/* Disclaimer */}
        <View style={styles.disclaimer}>
          <Text>
            CONFIDENTIAL - PROTECTED HEALTH INFORMATION: This document contains protected health
            information under HIPAA. Unauthorized disclosure or use is prohibited. This session note
            is for billing, clinical, and compliance purposes only.
          </Text>
        </View>

        {/* Footer */}
        <Text style={styles.footer}>
          Page 1 of 1 | Document ID: {note.id} | Status: {note.status.toUpperCase()}
          {'\n'}
          This document was generated electronically and is valid without a physical signature.
        </Text>
      </Page>
    </Document>
  );
};

// Service Class
class PDFExportService {
  /**
   * Generate and download PDF for a session note
   */
  async exportSessionNoteToPDF(
    note: SessionNote,
    companyName?: string,
    companyLogo?: string
  ): Promise<void> {
    try {
      // Generate PDF
      const blob = await pdf(
        <SessionNotePDF note={note} companyName={companyName} companyLogo={companyLogo} />
      ).toBlob();

      // Create filename
      const clientName = note.clientId || 'client';
      const date = note.timestamps.sessionStart
        ? note.timestamps.sessionStart.toDate().toISOString().split('T')[0]
        : 'unknown-date';
      const filename = `session-note_${clientName}_${date}_${note.id.substring(0, 8)}.pdf`;

      // Download
      saveAs(blob, filename);

      console.log(`PDF exported successfully: ${filename}`);
    } catch (error) {
      console.error('Error exporting PDF:', error);
      throw new Error('Failed to export PDF. Please try again.');
    }
  }

  /**
   * Generate PDF blob (for upload to storage instead of download)
   */
  async generatePDFBlob(
    note: SessionNote,
    companyName?: string,
    companyLogo?: string
  ): Promise<Blob> {
    try {
      const blob = await pdf(
        <SessionNotePDF note={note} companyName={companyName} companyLogo={companyLogo} />
      ).toBlob();

      return blob;
    } catch (error) {
      console.error('Error generating PDF blob:', error);
      throw new Error('Failed to generate PDF.');
    }
  }
}

export const pdfExportService = new PDFExportService();
